import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import i18n from 'modules/core/lib/i18n'

import { castError } from 'modules/expenses/components/expenses/Detail/_newForm/castError'
import withCustomizationFields, {
  WithCustomizationFieldsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import withFieldPermissions, {
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { DateTimeSelector } from 'modules/performance_process/components/common/DateTimeSelector'

interface Props extends WithCustomizationFieldsProps, WithFieldPermissionsProps {}

const START_DATE_FIELD_NAME = 'start_date'
const END_DATE_FIELD_NAME = 'end_date'

const DateTimeFieldComponent = ({ isFieldReadOnly, isFieldMandatory, fieldIdentifier }: Props) => {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext()

  const isStartDate = fieldIdentifier === START_DATE_FIELD_NAME
  const fieldName = isStartDate ? START_DATE_FIELD_NAME : END_DATE_FIELD_NAME

  // Watch the start date to make minDate reactive for end date field
  const startDate = useWatch({
    control,
    name: START_DATE_FIELD_NAME,
  })

  const getPlaceholder = () => {
    return isStartDate
      ? i18n.t('expenses_v2.forms.fields.start_date.placeholder')
      : i18n.t('expenses_v2.forms.fields.end_date.placeholder')
  }

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => {
        const handleStartDateChange = (date: Date) => {
          const dateString = date.toISOString()
          setValue(fieldName, dateString, { shouldDirty: true, shouldValidate: true })

          // Apply the logic from PerDiemFields: if this is start_date and end_date is before start_date or empty, set end_date to next day
          const endDate = getValues(END_DATE_FIELD_NAME)

          if (!endDate || new Date(endDate) < date) {
            const datePlusOneDay = new Date(date)
            if (!endDate) datePlusOneDay.setDate(datePlusOneDay.getDate() + 1)
            setValue(END_DATE_FIELD_NAME, datePlusOneDay.toISOString(), {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        }

        const handleEndDateChange = (date: Date) => {
          const dateString = date.toISOString()
          setValue(fieldName, dateString, { shouldDirty: true, shouldValidate: true })

          // Handle case when end_date is set to be before start_date
          const currentStartDate = getValues(START_DATE_FIELD_NAME)

          if (currentStartDate && date < new Date(currentStartDate)) {
            const newStartDate = new Date(currentStartDate)
            setValue(fieldName, newStartDate.toISOString(), {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        }

        const handleChange = (date: Date) => {
          if (isStartDate) {
            handleStartDateChange(date)
          } else {
            handleEndDateChange(date)
          }
        }

        return (
          <DateTimeSelector
            readonly={isFieldReadOnly}
            placeholder={getPlaceholder()}
            date={field.value}
            mandatory={isFieldMandatory}
            error={castError(errors?.[fieldName])}
            fullWidth
            onChange={handleChange}
            biggerTimeSelector
            minDate={!isStartDate ? startDate : undefined}
          />
        )
      }}
    />
  )
}

// Wrapper components that pass fieldIdentifier internally
const StartDateFieldWrapper = (props: WithCustomizationFieldsProps & WithFieldPermissionsProps) => (
  <DateTimeFieldComponent {...props} fieldIdentifier={START_DATE_FIELD_NAME} />
)

const EndDateFieldWrapper = (props: WithCustomizationFieldsProps & WithFieldPermissionsProps) => (
  <DateTimeFieldComponent {...props} fieldIdentifier={END_DATE_FIELD_NAME} />
)

// Export the wrapped components with HOCs
export const StartDateField = withCustomizationFields(
  withFieldPermissions(StartDateFieldWrapper, START_DATE_FIELD_NAME)
)

export const EndDateField = withCustomizationFields(
  withFieldPermissions(EndDateFieldWrapper, END_DATE_FIELD_NAME)
)
